// Google Ads (gtag.js) — carregado SÓ nas páginas públicas + confirmação de pagamento.
//
// O app é um SPA: colar a tag no <head> do index.html a faria rodar também no
// portal (projetos, documentos, dados pessoais). Por isso a tag é injetada sob
// demanda pelo <GoogleAdsTracker /> e a URL enviada ao Google é sempre
// origin + pathname (sem query/hash: oobCode, payment_intent_client_secret,
// e-mail etc. nunca saem daqui).
import { LANDING_PAGES } from "./landingContent";

export const GADS_ID = "AW-16632725354";
// Rótulo da ação de conversão "Compra" (Google Ads → Metas → Conversões →
// a ação → "Configuração da tag" → send_to: 'AW-16632725354/<RÓTULO>').
// Sem ele a conversão não é enviada (só os page_views).
const PURCHASE_LABEL = process.env.REACT_APP_GADS_PURCHASE_LABEL || "";
const NEW_CUSTOMER_URL =
  "https://us-central1-alpha-translations.cloudfunctions.net/checkNewCustomer";

const LANG_PREFIXES = ["", "/es", "/en"];
const PUBLIC_PATHS = new Set([
  ...LANG_PREFIXES.map((p) => p || "/"),
  ...LANG_PREFIXES.flatMap((p) => [`${p}/register`, `${p}/login`, `${p}/terms-of-use`]),
  ...LANG_PREFIXES.flatMap((p) => LANDING_PAGES.map((l) => `${p}${l.path}`)),
  "/affiliates/register",
  "/affiliates/login",
  // Página de "pedido concluído" — recebe a conversão.
  "/client/payment-success",
]);

const normalizePath = (pathname) =>
  pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

export const isTrackedPath = (pathname) => PUBLIC_PATHS.has(normalizePath(pathname || "/"));

const sanitizeUrl = (url) => {
  try {
    const u = new URL(url, window.location.origin);
    return `${u.origin}${u.pathname}`;
  } catch {
    return "";
  }
};

let loaded = false;
let enabled = false;

const ensureLoaded = () => {
  if (loaded || typeof window === "undefined") return;
  loaded = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GADS_ID}`;
  document.head.appendChild(s);
  window.gtag("js", new Date());
  window.gtag("config", GADS_ID, {
    send_page_view: false, // page_view manual, com URL saneada
    page_location: sanitizeUrl(window.location.href),
    page_referrer: sanitizeUrl(document.referrer),
  });
};

const setEnabled = (on) => {
  if (!loaded || enabled === on) return;
  enabled = on;
  window[`ga-disable-${GADS_ID}`] = !on;
};

// Chamado a cada mudança de rota. Fora das rotas públicas a tag nem é
// carregada; se já estava carregada (veio do site público e fez login), fica
// desativada até voltar a uma rota pública.
export const handleRouteChange = (pathname, prevPathname) => {
  if (!isTrackedPath(pathname)) {
    setEnabled(false);
    return;
  }
  ensureLoaded();
  setEnabled(true);
  const location = `${window.location.origin}${normalizePath(pathname)}`;
  window.gtag("event", "page_view", {
    send_to: GADS_ID,
    page_location: location,
    page_referrer: prevPathname
      ? isTrackedPath(prevPathname)
        ? `${window.location.origin}${normalizePath(prevPathname)}`
        : window.location.origin
      : sanitizeUrl(document.referrer),
    page_title: document.title,
  });
};

// 1ª compra paga do cliente (empresa, no b2b)? Decidido no backend a partir do
// Stripe + Firestore. null se não der para saber — aí o parâmetro é omitido.
const fetchNewCustomer = async (paymentIntentId) => {
  if (!/^pi_/.test(String(paymentIntentId))) return null;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 5000);
  try {
    const r = await fetch(NEW_CUSTOMER_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ paymentIntentId }),
      signal: ctrl.signal,
    });
    if (!r.ok) return null;
    const { newCustomer } = await r.json();
    return typeof newCustomer === "boolean" ? newCustomer : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};

// Conversão de compra. `transactionId` (id do PaymentIntent) deduplica no
// Google Ads; o sessionStorage evita reenviar no F5 da página de sucesso.
export const trackPurchaseConversion = async ({ transactionId, value, currency }) => {
  if (!PURCHASE_LABEL || !transactionId) return;
  const key = `gads_conv_${transactionId}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch {
    /* sem storage: o transaction_id ainda deduplica no Google */
  }
  ensureLoaded();
  const newCustomer = await fetchNewCustomer(transactionId);
  setEnabled(true);
  window.gtag("event", "conversion", {
    send_to: `${GADS_ID}/${PURCHASE_LABEL}`,
    value: Number(value) || 0,
    currency: String(currency || "usd").toUpperCase(),
    transaction_id: String(transactionId),
    ...(newCustomer === null ? {} : { new_customer: newCustomer }),
    page_location: sanitizeUrl(window.location.href),
  });
};
